<template>
    <panel no-button="true" :title="`Import ${panelTitle}`">
        <div class="form-group row m-b-15" v-if="!$route.params.level && importLevel || !importLevel">
            <label class="col-form-label col-md-3">Import level</label>
            <div class="col-md-9">
                <select v-model="importLevel" class="form-control">
                    <option value="disabled" selected disabled>Select level to import</option>
                    <option value="storyboard">Storyboard</option>
                    <option value="stage">Stage</option>
                    <option value="step">Step</option>
                </select>
            </div>
        </div>
        <div class="form-group row m-b-15">
            <label class="col-form-label col-md-3">Import file (xlsx, xls, csv)</label>
            <div class="col-md-9">
                <b-form-file
                    v-model="file"
                    :state="Boolean(file)"
                    placeholder="Choose a file or drop it here..."
                    drop-placeholder="Drop file here..."
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    @change="handleFile"
                />
            </div>
        </div>
        <div v-if="file">
            <hr>
            <b-overlay :show="isLoading" rounded="sm">
                <div v-if="multipleSheets" class="form-group row m-b-15">
                    <label class="col-form-label col-md-3">Select sheet</label>
                    <div class="col-md-9">
                        <select v-model="selectedSheetName" class="form-control" @change="selectSheet">
                            <option value="null" selected disabled>Select sheet</option>
                            <option v-for="(sheet, index) in Object.keys(workbook.Sheets)" :key="index" :value="sheet">{{ sheet }}</option>
                        </select>
                        <p v-if="selectedSheetName && !headers.length" class="text-red">This sheet is empty!</p>
                    </div>
                </div>
                <div v-if="selectedSheet">
                    <div class="form-group row m-b-15">
                        <label class="col-form-label col-md-3">Name column</label>
                        <div class="col-md-9">
                            <select v-model="nameColumn" class="form-control">
                                <option value="disabled" selected disabled>Select column</option>
                                <option value="null">None</option>
                                <option v-for="(header, index) in headers" :key="index" :value="header">{{ header }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="form-group row m-b-15">
                        <label class="col-form-label col-md-3">Description column</label>
                        <div class="col-md-9">
                            <select v-model="descriptionColumn" class="form-control">
                                <option value="disabled" selected disabled>Select column</option>
                                <option value="null">None</option>
                                <option v-for="(header, index) in headers" :key="index" :value="header">{{ header }}</option>
                            </select>
                        </div>
                    </div>
                    <hr>
                    <div class="form-group row m-b-15" v-if="$route.params.level == 'stages'">
                        <label class="col-form-label col-md-3">Attach all to storyboard</label>
                        <div class="col-md-9">
                            <select v-model="parentStoryboard" class="form-control">
                                <option :value="null">None</option>
                                <option v-for="storyboard in storyboards" :key="storyboard.id" :value="storyboard.id">{{ storyboard.name }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="form-group row m-b-15" v-if="$route.params.level == 'steps'">
                        <label class="col-form-label col-md-3">Attach all to stage</label>
                        <div class="col-md-9">
                            <select v-model="parentStage" class="form-control">
                                <option :value="null">None</option>
                                <option v-for="stage in stages" :key="stage.id" :value="stage.id">{{ stage.name }}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </b-overlay>
        </div>

        <hr />

        <url-button label="Back" :url="'/storyboard'" class="btn-secondary" />
        <input-button :disabled="!headers.length || nameColumn == 'disabled'" type="submit" label="Import" @click="validate" class="btn-yellow pull-right" />
    
        <b-modal ref="modalItems" centered title="Items imported" ok-only ok-variant="yellow" v-on:ok="close">
            <div class="alert alert-secondary m-b-0">
                <h5><i class="fa fa-info-circle"></i>Items imported!</h5>
                <p>Items imported successfully!</p>
            </div>
            <div v-if="errors.length" class="alert alert-danger m-b-0 m-t-20">
                <h5><i class="fa fa-info-circle"></i>Invalid items!</h5>
                <p>These items were not imported!</p>
                <b-button variant="light" @click="toggleErrors">{{ showErrors ? "Hide errors" : "Show errors" }}</b-button>
                <div class="mt-2" v-if="showErrors">
                    <vue-custom-scrollbar v-if="errors.length > 15" class="height-sm">
                        <ul>
                            <li v-for="(error, index) in errors" :key="index">{{ error.error }}</li>
                        </ul>
                    </vue-custom-scrollbar>
                    <div v-else>
                        <ul>
                            <li v-for="(error, index) in errors" :key="index">{{ error.error }}</li>
                        </ul>
                    </div>
                </div>
            </div>
        </b-modal>
    </panel>
</template>

<script>
import XLSX from 'xlsx'
import UrlButton from '../buttons/UrlButton.vue'
import InputButton from '../buttons/InputButton.vue'

export default {
    name: 'StoryboardImport',
    props: ['storyboards', 'stages', 'activeEvent'],
    components: {
        UrlButton,
        InputButton
    },
    data() {
        return {
            importLevel: 'disabled',
            file: null,
            isLoading: false,
            workbook: null,
            multipleSheets: false,
            selectedSheetName: null,
            selectedSheet: null,
            headers: [],
            nameColumn: 'disabled',
            descriptionColumn: 'disabled',
            items: [],
            errors: [],
            showErrors: false,
            parentStoryboard: null,
            parentStage: null
        }
    },
    computed: {
        panelTitle() {
            switch (this.$route.params.level) {
                case 'storyboards':
                    return 'Storyboard(s)'
                case 'stages':
                    return 'Stage(s)'
                case 'steps':
                    return 'Step(s)'
                default:
                    return ''
            }
        }
    },
    methods: {
        handleFile(e) {
            this.selectedSheetName = null;
            this.headers = [];
            this.isLoading = true;
            let self = this;
            let files = e.target.files, f = files[0];
            let reader = new FileReader();
            reader.onload = function(e) {
                let data = new Uint8Array(e.target.result);
                self.workbook = XLSX.read(data, { type: 'array' });
                if (self.workbook.SheetNames.length > 1) {
                    self.multipleSheets = true;
                } else {
                    self.multipleSheets = false;
                    self.selectedSheetName = self.workbook.SheetNames[0];
                    self.selectSheet();
                }
            };
            reader.readAsArrayBuffer(f);
            this.isLoading = false;
        },
        selectSheet() {
            this.headers = [];
            this.selectedSheet = this.workbook.Sheets[this.selectedSheetName];
            if (this.selectedSheet['!ref']) {
                let columnCount = XLSX.utils.decode_range(this.selectedSheet['!ref']).e.c + 1;
                for (let i = 0; i < columnCount; ++i) {
                    this.headers[i] = this.selectedSheet[`${XLSX.utils.encode_col(i)}1`].v;
                }
            }
        },
        validate() {
            this.items = [];
            let item = null;
            if (this.selectedSheet['!ref']) {
                let rowCount = XLSX.utils.decode_range(this.selectedSheet['!ref']).e.r + 1;
                let columnCount = XLSX.utils.decode_range(this.selectedSheet['!ref']).e.c + 1;
                for (let i = 1; i < rowCount; ++i) {
                    item = {
                        name: null,
                        description: ''
                    };
                    for (let j = 0; j < columnCount; ++j) {
                        switch (this.headers[j]) {
                            case this.nameColumn:
                                if (this.selectedSheet[`${XLSX.utils.encode_col(j)}${XLSX.utils.encode_row(i)}`]) {
                                    item.name = this.selectedSheet[`${XLSX.utils.encode_col(j)}${XLSX.utils.encode_row(i)}`].v;
                                }
                                break;
                            case this.descriptionColumn:
                                if (this.selectedSheet[`${XLSX.utils.encode_col(j)}${XLSX.utils.encode_row(i)}`]) {
                                    item.description = this.selectedSheet[`${XLSX.utils.encode_col(j)}${XLSX.utils.encode_row(i)}`].v;
                                }
                                break;
                            default:
                                break;
                        }
                    }
                    this.items.push(item);
                }
            }
            this.submit();
        },
        submit() {
            this.errors = [];
            if (this.importLevel) {
                this.items.forEach((item, index) => {
                    if (item.name) {
                        switch (this.importLevel) {
                            case 'storyboard':
                                this.$parent.create(JSON.stringify({
                                    name: item.name,
                                    description: item.description,
                                    notes: [],
                                    eventId: parseInt(this.activeEvent.id)
                                }), false);
                                break;
                            case 'stage':
                                this.$parent.stageCreate(JSON.stringify({
                                    name: item.name,
                                    description: item.description,
                                    notes: [],
                                    storyboard: this.parentStoryboard ? parseInt(this.parentStoryboard) : null,
                                    eventId: parseInt(this.activeEvent.id)
                                }), false);
                                break;
                            case 'step':
                                this.$parent.stepCreate(JSON.stringify({
                                    name: item.name,
                                    description: item.description,
                                    notes: [],
                                    stage: this.parentStage ? parseInt(this.parentStage) : null,
                                    eventId: parseInt(this.activeEvent.id)
                                }), false);
                                break;
                            default:
                                break;
                        }
                    } else {
                        this.errors.push({
                            error: "Item in the row " + (index + 2) + " has no name!"
                        });
                    }
                });
            } else {
                this.errors.push({
                    error: "Choose storyboard level to import."
                });
            }
            this.$refs['modalItems'].show();
        },
        toggleErrors() {
            this.showErrors = !this.showErrors;
        },
        close() {
            this.file = null;
            this.nameColumn = 'disabled';
            this.descriptionColumn = 'disabled';
            this.parentStoryboard = null;
            this.parentStage = null;
        },
        setLevelValues() {
            switch (this.$route.params.level) {
                case 'storyboards':
                    this.importLevel = 'storyboard'
                    break;
                case 'stages':
                    this.importLevel = 'stage'
                    break;
                case 'steps':
                    this.importLevel = 'step'
                    break;
                default:
                    break;
            }
        }
    },
    mounted() {
        this.setLevelValues();
    }
}
</script>